/**
 * Persists the “folder as workspace” root via IndexedDB (FileSystemDirectoryHandle is not JSON-safe).
 * Same-origin only; user may still need to re-grant permission after reload depending on browser.
 */

const DB_NAME = "npp-web-session";
const DB_VERSION = 1;
const STORE = "handles";
const KEY_ROOT = "workspaceDirectory";

function idbAvailable(): boolean {
  return typeof indexedDB !== "undefined";
}

function openDb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onerror = (): void => reject(req.error ?? new Error("indexedDB.open failed"));
    req.onblocked = (): void => reject(new Error("indexedDB blocked"));
    req.onupgradeneeded = (): void => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE)) {
        db.createObjectStore(STORE);
      }
    };
    req.onsuccess = (): void => resolve(req.result);
  });
}

/** Save or clear the workspace folder handle (null clears). */
export async function persistWorkspaceFolderHandle(
  handle: FileSystemDirectoryHandle | null,
): Promise<void> {
  if (!idbAvailable()) return;
  let db: IDBDatabase | null = null;
  try {
    db = await openDb();
    await new Promise<void>((resolve, reject) => {
      const tx = db!.transaction(STORE, "readwrite");
      tx.oncomplete = (): void => resolve();
      tx.onerror = (): void => reject(tx.error ?? new Error("idb tx"));
      tx.onabort = (): void => reject(tx.error ?? new Error("idb abort"));
      const store = tx.objectStore(STORE);
      if (handle === null) {
        store.delete(KEY_ROOT);
      } else {
        store.put(handle, KEY_ROOT);
      }
    });
  } catch {
    /* private mode, quota, unsupported */
  } finally {
    try {
      db?.close();
    } catch {
      /* ignore */
    }
  }
}

/** Load a previously stored workspace directory handle, if any. */
export async function loadWorkspaceFolderHandle(): Promise<FileSystemDirectoryHandle | null> {
  if (!idbAvailable()) return null;
  let db: IDBDatabase | null = null;
  try {
    db = await openDb();
    return await new Promise<FileSystemDirectoryHandle | null>((resolve, reject) => {
      const tx = db!.transaction(STORE, "readonly");
      tx.onerror = (): void => reject(tx.error ?? new Error("idb tx"));
      const store = tx.objectStore(STORE);
      const r = store.get(KEY_ROOT);
      r.onsuccess = (): void => {
        const v = r.result;
        resolve(
          v && typeof (v as FileSystemDirectoryHandle).queryPermission === "function"
            ? (v as FileSystemDirectoryHandle)
            : null,
        );
      };
      r.onerror = (): void => reject(r.error ?? new Error("idb get"));
    });
  } catch {
    return null;
  } finally {
    try {
      db?.close();
    } catch {
      /* ignore */
    }
  }
}
